import React from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { BackButton } from "../../components/common/MainButtonComponent";
import ContentWrapper from "../../components/common/AssessmentContentWrapper";
import { routes } from "../../helper/routes";
import "./AssessShared.css";
import "./SetupAssessment.css";

const getDisplayText = (value, fallback = "N/A") => {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }

  if (Array.isArray(value)) {
    return value.length ? value.join(", ") : fallback;
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  return String(value);
};

const getQuestionContentText = (value) => {
  if (value === null || value === undefined) return "No question content available.";
  if (typeof value !== "string") return String(value);

  const trimmedValue = value.trim();
  if (!trimmedValue) return "No question content available.";

  if (trimmedValue.startsWith("{") || trimmedValue.startsWith("[")) {
    try {
      const parsedValue = JSON.parse(trimmedValue);
      const firstValue = Array.isArray(parsedValue) ? parsedValue[0] : parsedValue;
      if (firstValue?.blocks && Array.isArray(firstValue.blocks)) {
        return firstValue.blocks
          .map((block) => block?.text || "")
          .filter(Boolean)
          .join("\n")
          .trim() || "No question content available.";
      }
    } catch {
      return trimmedValue;
    }
  }

  return trimmedValue;
};

const formatCreatedAt = (value) => {
  if (value === null || value === undefined || value === "") {
    return "N/A";
  }

  const normalizedValue = String(value).replace(/\.(\d{3})\d+/, ".$1");
  const parsedDate = new Date(normalizedValue);

  if (Number.isNaN(parsedDate.getTime())) {
    return String(value);
  }

  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  })
    .format(parsedDate)
    .replace(/(\d{2})\s([A-Za-z]{3})\s(\d{4}),\s(\d{1,2}):(\d{2})\s([AP]M)/, "$1 $2 $3, $4:$5 $6");
};

const normalizeQuestionViewModel = (question, questionId) => {
  const normalized = question || {};
  return {
    id: normalized.questionId ?? normalized.id ?? questionId ?? null,
    code: normalized.code ?? normalized.question_code ?? normalized.title ?? normalized.name ?? `Q${normalized.questionId ?? normalized.id ?? questionId ?? ""}`,
    name: normalized.name ?? normalized.title ?? normalized.question_title ?? normalized.code ?? "Question",
    type: normalized.type ?? normalized.question_type_name ?? normalized.question_type ?? "N/A",
    language: normalized.language ?? normalized.language_name ?? "English",
    grade: normalized.grade ?? normalized.grade_name ?? "N/A",
    createdBy: normalized.createdBy ?? normalized.created_by ?? "N/A",
    createdAt: normalized.createdAt ?? normalized.created_at ?? "N/A",
    content: normalized.content ?? normalized.question_content ?? normalized.name ?? normalized.title ?? "",
    answer: normalized.answer ?? normalized.modelAnswer ?? normalized.model_answer ?? "",
    answer_type_id: normalized.answer_type_id ?? null,
    sub_questions_details: Array.isArray(normalized.sub_questions_details)
      ? normalized.sub_questions_details
      : [],
  };
};

export default function SetupAssessmentQuestionViewPage({
  setOpenQuestionView,
  question: providedQuestion,
}) {
  const history = useHistory();
  const location = useLocation();
  const { id, questionId } = useParams();

  const question = normalizeQuestionViewModel(
    providedQuestion || location.state?.question,
    questionId
  );
  const cameFromUpdateDrawer = Boolean(location.state?.fromUpdateDrawer);
  const drawerState = location.state?.drawerState;

  const goBack = () => {
    if (typeof setOpenQuestionView === "function") {
      setOpenQuestionView(false);
      return;
    }

    if (cameFromUpdateDrawer && drawerState) {
      history.push({
        pathname: `${routes.asses_lib}/setup/${id}`,
        state: {
          drawerState,
        },
      });
      return;
    }

    history.push(`${routes.asses_lib}/setup/${id}`);
  };

  return (
    <ContentWrapper>
      <section className="assess-section setup-question-view-page">
        <div className="setup-meta-page__actions">
          <BackButton label="Back" onClick={goBack} />
        </div>

        <div className="setup-question-view-card">
          <div className="setup-question-view-card__header">
            <div>
              <span>Question Details</span>
              <h1>{question.name || question.code || questionId}</h1>
            </div>
          </div>

          <div className="setup-question-view-card__body">
            <div className="setup-question-view-block">
              <h2>Question:</h2>
              <p>{getQuestionContentText(question.content)}</p>
            </div>

            <div className="setup-question-view-block">
              <h2>Model Answer:</h2>
              <p>{getQuestionContentText(question.answer) || "Model answer not available."}</p>
            </div>

            <div className="setup-question-view-grid">
              <div className="setup-question-view-item">
                <span>Type :</span>
                <strong>{getDisplayText(question.type)}</strong>
              </div>
              <div className="setup-question-view-item">
                <span>Language :</span>
                <strong>{getDisplayText(question.language, "English")}</strong>
              </div>
              <div className="setup-question-view-item">
                <span>Grade :</span>
                <strong>{getDisplayText(question.grade)}</strong>
              </div>
              <div className="setup-question-view-item">
                <span>Created By :</span>
                <strong>{getDisplayText(question.createdBy)}</strong>
              </div>
              <div className="setup-question-view-item">
                <span>Created At :</span>
                <strong>{formatCreatedAt(question.createdAt)}</strong>
              </div>
            </div>
          </div>
        </div>
      </section>
    </ContentWrapper>
  );
}
